import React, { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { Eye } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DocumentRequestReason,
  DocumentRequestStatus,
  ReasonOption,
  reasonOptions,
  reasonsFieldsMap,
  reasonsMap,
  statusMap,
  tenantNextStatusMap,
} from '@area-restrita/shared/document/document-requests';
import { DocumentRequestDetailQueryResponse } from '@area-restrita/api-client/model/documentRequestDetailQueryResponse';
import { useUpdateDocumentRequest } from '@area-restrita/api-client/endpoints/document-request/document-request.gen';

import InlineInput from '@/components/InlineInput';
import PageContainer from '@/components/layout/PageContainer';
import DocumentSelector from '@/components/QuerySelectors/DocumentSelector';
import Select, { SelectOption } from '@/components/Select';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Form, FormField } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';
import FileInput from '@/components/FileInput';

import {
  TenantDocumentRequestDetailsFormSchema,
  inRequestSchema,
  schema,
} from './schema';

interface IProps {
  documentRequest: DocumentRequestDetailQueryResponse;
}

const TenantDocumentRequestDetails: React.FC<IProps> = ({
  documentRequest,
}) => {
  const canUploadDocument = useMemo(
    () =>
      documentRequest.status === 'request' ||
      documentRequest.status === 'production',
    [documentRequest.status],
  );

  const inRequestStatus = useMemo(
    () => documentRequest.status === 'request',
    [documentRequest.status],
  );

  const statusOptions = useMemo(
    () =>
      tenantNextStatusMap[documentRequest.status].map(
        (status) => statusMap[status],
      ),
    [documentRequest.status],
  );

  const readOnly = statusOptions.length <= 1;

  const navigate = useNavigate();
  const { checkAuthorization } = useAuthSessionStore();
  const canUpdate = checkAuthorization(({ can }) =>
    can('update', 'document_requests'),
  );

  const form = useForm<TenantDocumentRequestDetailsFormSchema>({
    defaultValues: {
      hasUploadedFile: documentRequest.hasUploadedFile,

      justification: documentRequest.justification,
      status: statusMap[documentRequest.status],
      document: {
        label: documentRequest.document.name,
        value: documentRequest.document.publicId,
      },
      reason: reasonsMap[documentRequest.reason as DocumentRequestReason],
      reasonFields: documentRequest.reasonFields as Record<string, string>,
      dateForApproval: format(
        parseISO(documentRequest.implantationDate),
        'yyyy-MM-dd',
      ),
      observation: documentRequest.observation,
    },
    resolver: zodResolver(inRequestStatus ? inRequestSchema : schema),
  });

  const { control, watch } = form;

  const reason = watch('reason') as ReasonOption;

  const reasonFields = useMemo(
    () => (reason ? reasonsFieldsMap[reason.value].fields : []),
    [reason],
  );

  const { mutateAsync } = useUpdateDocumentRequest({
    mutation: {
      onSuccess: () => {
        toast.success('Solicitação de documento atualizada com sucesso');
        navigate({ to: '/document-requests' });
      },
      onError: () => {
        toast.error('Erro ao atualizar a solicitação de documento');
      },
    },
  });

  const handlerSubmit = useCallback<
    SubmitHandler<TenantDocumentRequestDetailsFormSchema>
  >(
    ({ status, reason, dateForApproval, ...data }) =>
      mutateAsync({
        id: documentRequest.id,
        data: {
          status: status.value as DocumentRequestStatus,
          reason: reason.value,
          implantationDate: dateForApproval,
          ...data,
        },
      }),
    [mutateAsync, documentRequest.id],
  );

  return (
    <PageContainer
      title="Detalhes da solicitação de documento"
      subtitle={`Solicitação criada em ${format(
        parseISO(documentRequest.createdAt),
        'dd/MM/yyyy',
      )}`}
    >
      <Card>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handlerSubmit, console.log)}>
            <CardContent className="flex flex-col gap-6 pt-6">
              {!inRequestStatus && (
                <FormField
                  control={form.control}
                  name="file"
                  render={({ field }) => (
                    <InlineInput
                      title="Arquivo"
                      as={FileInput}
                      {...field}
                      className={!canUploadDocument ? 'hidden' : undefined}
                      placeholder={
                        documentRequest.hasUploadedFile ? (
                          <>
                            Clique para adicionar um arquivo <br />
                            <span className="text-sm">
                              Deixe em branco para manter o arquivo atual
                            </span>
                          </>
                        ) : undefined
                      }
                    >
                      {documentRequest.hasUploadedFile && !field.value && (
                        <Link
                          to="/docs/document-request/$documentRequestId"
                          params={{ documentRequestId: documentRequest.id }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Visualizar documento atual
                          </Button>
                        </Link>
                      )}
                    </InlineInput>
                  )}
                />
              )}

              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <InlineInput
                      title="Status"
                      as={(props: {
                        value: SelectOption;
                        onChange: (value: SelectOption) => void;
                      }) => (
                        <Select
                          options={statusOptions}
                          {...props}
                          multiple={false}
                        />
                      )}
                      disabled={readOnly}
                      {...field}
                    />
                  )}
                />

                {!inRequestStatus && (
                  <FormField
                    control={control}
                    name="justification"
                    render={({ field }) => (
                      <InlineInput
                        title="Justificativa"
                        as={Textarea}
                        disabled={readOnly}
                        // disabled={isLoading}
                        {...field}
                      />
                    )}
                  />
                )}
              </div>

              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name="document"
                  render={({ field }) => (
                    <InlineInput
                      title="Documento"
                      as={(props: {
                        value: SelectOption;
                        onChange: (value: SelectOption) => void;
                      }) => (
                        <DocumentSelector
                          {...props}
                          multiple={false}
                          allowsChangeRequest
                          placeholder="Selecione o documento a ser alterado"
                          readOnly
                          disabled
                        />
                      )}
                      disabled={readOnly}
                      {...field}
                    />
                  )}
                />
                <FormField
                  control={control}
                  name="reason"
                  render={({ field }) => (
                    <InlineInput
                      title="Motivo"
                      as={(props: {
                        value: SelectOption;
                        onChange: (value: SelectOption) => void;
                      }) => (
                        <Select
                          options={reasonOptions}
                          {...props}
                          multiple={false}
                          readOnly
                          disabled
                        />
                      )}
                      disabled={readOnly}
                      {...field}
                    />
                  )}
                />
                {reasonFields.map(({ name, label }) => (
                  <FormField
                    key={`${reason?.value}-${name}`}
                    control={control}
                    name={`reasonFields.${name}`}
                    render={({ field }) => (
                      <InlineInput
                        id={`${reason?.value}-${name}`}
                        title={label}
                        disabled={readOnly}
                        // disabled={isLoading}
                        {...field}
                        value={field.value || ''}
                      />
                    )}
                  />
                ))}
              </div>

              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name="dateForApproval"
                  render={({ field }) => (
                    <InlineInput
                      title="Data para aprovação"
                      type="date"
                      disabled={readOnly}
                      // disabled={isLoading}
                      {...field}
                    />
                  )}
                />
                <FormField
                  control={control}
                  name="observation"
                  render={({ field }) => (
                    <InlineInput
                      title="Observações"
                      as={Textarea}
                      disabled={readOnly}
                      // disabled={isLoading}
                      {...field}
                    />
                  )}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button type="submit" disabled={!canUpdate || readOnly}>
                Salvar
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </PageContainer>
  );
};

export default TenantDocumentRequestDetails;
